import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import {
    FormContainer,
    SelectElement,
    TextFieldElement,
} from 'react-hook-form-mui';
import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Stack,
} from '@mui/material';

import { EditButtons } from '@barboxx/admin-components';
import { PRODUCT_ATTRIBUTE_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { ProductAttribute } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';
import { PRODUCT_ATTRIBUTE_TYPES } from '@barboxx/types';
import { enumToFormOptions } from '@barboxx/utils';

interface Props {
    productAttribute: ProductAttribute.DetailsResponse;
}

export const AttributeDetailsCard = ({ productAttribute }: Props) => {
    const { attributeValues, ...rest } = productAttribute;
    const [editable, editableActions] = useBool(false);
    const notifications = useNotifications();

    const formContext = useForm<ProductAttribute.UpdateRequest>({
        resolver: yupResolver(PRODUCT_ATTRIBUTE_SCHEMA),
        defaultValues: rest,
    });

    const mutateUser = useMutation({
        mutationKey: ['productAttribute', productAttribute.id],
        mutationFn: (data: ProductAttribute.UpdateRequest) =>
            Api.Products.ProductAttributes.putProductAttribute(
                productAttribute.id,
                data
            ),
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: ProductAttribute.UpdateRequest) => {
        return mutateUser
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show('Updated product attribute!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notifications.show('Error Updating product attribute!', {
                    severity: 'error',
                });
            });
    };

    return (
        <FormContainer onSuccess={onSubmit} formContext={formContext}>
            <Card>
                <CardHeader title="Product Attribute Details" />
                <CardContent>
                    <Stack gap={2}>
                        <TextFieldElement
                            fullWidth
                            name="name"
                            disabled={!editable}
                            label="Name"
                        />
                        <TextFieldElement
                            fullWidth
                            name="description"
                            disabled={!editable}
                            label="Description"
                        />
                        <SelectElement
                            label="Type"
                            name="attributeType"
                            options={enumToFormOptions(PRODUCT_ATTRIBUTE_TYPES)}
                            fullWidth
                            disabled={!editable}
                        />
                    </Stack>
                </CardContent>
                <CardActions
                    sx={{
                        justifyContent: 'flex-end',
                        paddingRight: 3,
                        paddingBottom: 3,
                    }}
                >
                    <Can I="update" a="Product">
                        <EditButtons
                            formContext={formContext}
                            handleCancel={handleCancel}
                            editable={editable}
                            editableActions={editableActions}
                        />
                    </Can>
                </CardActions>
            </Card>
        </FormContainer>
    );
};
